import { useEffect, useMemo, useRef, useState } from 'react'
import { getInitialViewByWidth, MAPBOX_TOKEN, mapStyle, parseCoordinate } from './utils'
import MapGL, { MapProvider, NavigationControl, FullscreenControl, Source, Layer } from 'react-map-gl/mapbox'
import ControlGeocoder from './components/control-geocoder'
import Tooltip from './components/tooltip'
import TipologiesSelect from './components/TipologiesSelect'
import dadesJSON from './assets/refugis.json'
import textos from './assets/textos.json'
import 'mapbox-gl/dist/mapbox-gl.css'
import '@common/reset.css'
import './style.scss'

export default function App({ embedHome, lang, slug }) {
	const containerRef = useRef(null)
	const mapRef = useRef(null)
	const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
	const [refugiSeleccionat, setRefugiSeleccionat] = useState(null)
	const [canonicsSeleccionats, setCanonicsSeleccionats] = useState([])
	const [cursor, setCursor] = useState('default')
	const initialViewState = useMemo(() => {
		if (!containerSize.width || !containerSize.height) {
			return null
		}

		return getInitialViewByWidth(containerSize.width, containerSize.height)
	}, [containerSize.width, containerSize.height])
	const dades = useMemo(
		() =>
			dadesJSON.map((refugi) => ({
				...refugi,
				latitude: Number(refugi.latitud),
				longitude: Number(refugi.longitud),
			})),
		[],
	)
	const configuracioTipologies = useMemo(() => {
		return textos[lang]?.tipologia ?? textos.ca?.tipologia ?? {}
	}, [lang])

	const canonics = useMemo(() => Object.keys(configuracioTipologies), [configuracioTipologies])

	const tipusPerCanonic = useMemo(() => {
		return canonics.reduce((acc, canonic) => {
			const opcions = configuracioTipologies?.[canonic]?.opcions ?? []
			acc[canonic] = new Set(opcions)
			return acc
		}, {})
	}, [canonics, configuracioTipologies])

	const dadesFiltrades = useMemo(() => {
		if (!canonicsSeleccionats.length) {
			return []
		}

		return dades.filter((refugi) =>
			canonicsSeleccionats.some((canonic) => tipusPerCanonic[canonic]?.has(refugi.tipologia)),
		)
	}, [canonicsSeleccionats, dades, tipusPerCanonic])

	const geojson = useMemo(
		() => ({
			type: 'FeatureCollection',
			features: dadesFiltrades.map((refugi) => ({
				type: 'Feature',
				geometry: { type: 'Point', coordinates: [refugi.longitude, refugi.latitude] },
				properties: refugi,
			})),
		}),
		[dadesFiltrades],
	)
	const totsSeleccionats = canonicsSeleccionats.length === canonics.length && canonics.length > 0

	const resetTipologies = () => {
		setCanonicsSeleccionats(canonics)
	}

	const toggleCanonic = (canonic) => {
		setCanonicsSeleccionats((actuals) => {
			if (actuals.includes(canonic)) {
				if (actuals.length === canonics.length) {
					return [canonic]
				}

				return actuals.filter((canonicActual) => canonicActual !== canonic)
			}

			return [...actuals, canonic]
		})
	}

	const handleMapClick = (event) => {
		const feature = event.features?.[0]
		const map = mapRef.current?.getMap()
		if (!feature || !map) {
			setRefugiSeleccionat(null)
			return
		}

		if (feature.layer.id === 'clusters') {
			const source = map.getSource('refugis')
			source.getClusterExpansionZoom(feature.properties.cluster_id, (error, zoom) => {
				if (!error) map.easeTo({ center: feature.geometry.coordinates, zoom })
			})
			return
		}
	}

	const handleMapMouseMove = (event) => {
		const feature = event.features?.find(({ layer }) => ['clusters', 'unclustered-point'].includes(layer.id))
		setCursor(feature ? 'pointer' : undefined)
		const refugi = event.features?.find(({ layer }) => layer.id === 'unclustered-point')
		if (refugi) setRefugiSeleccionat(refugi.properties)
	}

	useEffect(() => {
		if (!containerRef.current) {
			return
		}

		const updateContainerSize = () => {
			const rect = containerRef.current?.getBoundingClientRect?.()
			setContainerSize({
				width: rect?.width ?? 0,
				height: rect?.height ?? 0,
			})
		}

		updateContainerSize()

		const resizeObserver = new ResizeObserver(updateContainerSize)
		resizeObserver.observe(containerRef.current)

		return () => {
			resizeObserver.disconnect()
		}
	}, [])

	useEffect(() => {
		setCanonicsSeleccionats(canonics)
	}, [canonics])

	useEffect(() => {
		if (!refugiSeleccionat) {
			return
		}

		const refugiVisible = canonicsSeleccionats.some((canonic) =>
			tipusPerCanonic[canonic]?.has(refugiSeleccionat.tipologia),
		)

		if (!refugiVisible) {
			setRefugiSeleccionat(null)
		}
	}, [canonicsSeleccionats, refugiSeleccionat, tipusPerCanonic])

	return (
		<MapProvider>
			<div className='container-filtres'>
				<p>{textos[lang]?.label_tipologia}:</p>
				<div className='filtres'>
					<TipologiesSelect
						lang={lang}
						textos={textos}
						canonics={canonics}
						canonicsSeleccionats={canonicsSeleccionats}
						totsSeleccionats={totsSeleccionats}
						configuracioTipologies={configuracioTipologies}
						onReset={resetTipologies}
						onToggle={toggleCanonic}
					/>
				</div>
			</div>
			<div className='container-mapa' ref={containerRef}>
				{initialViewState && (
					<MapGL
						id='mapa_refugis_climatics'
						ref={mapRef}
						mapboxAccessToken={MAPBOX_TOKEN}
						projection='mercator'
						cursor={cursor}
						initialViewState={initialViewState}
						style={{
							position: 'absolute',
							width: '100%',
							height: '100%',
						}}
						mapStyle={mapStyle}
						attributionControl={false}
						cooperativeGestures={true}
						reuseMaps
						locale={textos[lang]?.mapbox}
						onClick={handleMapClick}
						onMouseMove={handleMapMouseMove}
						onMouseLeave={() => {
							setCursor('default')
							setRefugiSeleccionat(null)
						}}
						interactiveLayerIds={['clusters', 'unclustered-point']}
					>
						<Source
							id='refugis'
							type='geojson'
							data={geojson}
							cluster
							clusterMaxZoom={10}
							clusterRadius={45}
						>
							<Layer
								id='clusters'
								type='circle'
								filter={['has', 'point_count']}
								paint={{
									'circle-color': '#0073ee',
									'circle-radius': 20,
									'circle-stroke-width': 2,
									'circle-stroke-color': '#fff',
								}}
							/>
							<Layer
								id='cluster-count'
								type='symbol'
								filter={['has', 'point_count']}
								layout={{
									'text-field': ['get', 'point_count_abbreviated'],
									'text-size': 14,
									'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
									'text-anchor': 'center',
									'text-offset': [0, 0],
								}}
								paint={{ 'text-color': '#fff' }}
							/>
							<Layer
								id='unclustered-point'
								type='circle'
								filter={['!', ['has', 'point_count']]}
								paint={{
									'circle-color': '#0073ee',
									'circle-radius': 7,
									'circle-stroke-width': 2,
									'circle-stroke-color': '#fff',
								}}
							/>
						</Source>
						{refugiSeleccionat && (
							<Tooltip
								refugi={refugiSeleccionat}
								onClose={() => setRefugiSeleccionat(null)}
								textos={textos[lang]}
							/>
						)}
						<ControlGeocoder accessToken={MAPBOX_TOKEN} />
						<FullscreenControl position='bottom-right' />
						<NavigationControl showZoom showCompass={false} />
					</MapGL>
				)}
			</div>

			<p className='font' dangerouslySetInnerHTML={{ __html: textos[lang]?.font }} />
		</MapProvider>
	)
}
